import pdfMake from 'pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import figureFormatter           from "@/services/utils/figureFormatter";
import { useStore } from "vuex";
import useDate from '@/services/utils/day';
import { formatMonthRangeLabel } from '@/services/utils/global';
import { isVisible, getActiveSetting, getPageMargins, resolveFontSize } from '@/services/utils/pdf/pdfCustomizationHelper';
pdfMake.vfs = pdfFonts.pdfMake.vfs;

pdfMake.fonts = {
    SulaimanLipi: {
        normal: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff',
        bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
        italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
        bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf'
    }
}

const pdfPrinter = () => {
    const { formatDate } = useDate();
    const store = useStore();
    const { commaFormat } = figureFormatter();

    const userName = store.state.user.user.name;
    const defaultPageMargins = [30, 100, 30, 80];
    const defaultFontSize = 10;
    const pageWidth = 841.89;

    const exportToPDF = (company, moneyReceipt, total, date, payload, customizationFields = {}) => {
        const pageMargins = getPageMargins(customizationFields, defaultPageMargins);

        var doc = {
            pageSize: 'A4',
            pageMargins: pageMargins,
            pageOrientation: 'landscape',
            header: getHeader(company, customizationFields, pageMargins),
            footer: getFooter(userName, customizationFields, pageMargins),
            content: getContent(moneyReceipt, total, date, payload, customizationFields),


            styles : {
                header: {
                    fontSize: 16
                }
            },

            defaultStyle: {
                color: 'black',
                fontSize: resolveFontSize(customizationFields, defaultFontSize),
                font: 'SulaimanLipi'
            },
            info: {
                title: getReportTitle(date, payload)
            }
        }

        const pdfDocGenerator = pdfMake.createPdf(doc);
        pdfDocGenerator.open();
    }

    const reportNames = { PAID: 'PAID STATEMENT', DUE: 'DUE STATEMENT' };

    const getReportTitle = (date, payload) => {
        const reportName = reportNames[payload.paymentStatus] || 'STATEMENT';
        const monthLabel = formatMonthRangeLabel(date.startDate, date.endDate);
        return monthLabel ? `${reportName} - ${monthLabel}` : reportName;
    }

    const getContent = (moneyReceipt, total, date, payload, customizationFields) => {
        const columns = getTableColumns(customizationFields, moneyReceipt);
        return [
            isVisible(customizationFields, 'body_top_left_report_title') ? {
                text: getReportTitle(date, payload),
                style: 'header',
                alignment: 'center',
                margin: [0, 0, 20, 10],
                bold: true,
                color: 'black',
                decoration: 'underline'
            } : null,

            {
                stack: getFilterLines(date, payload, customizationFields)
            },

            columns.length ? {
                style: 'tableExample',
                margin: [0, 10, 0, 0],
                table: {
                    widths: columns.map(column => column.width || 'auto'),
                    body: getTableBody(columns, moneyReceipt, total, customizationFields)
                },
                layout: {
                    fillColor: function (rowIndex, node, columnIndex) {
                        return (rowIndex === 0) ? '#f3f2f7' : null;
                    },
                    hLineWidth: function (i, node) {
                        return 0.5;
                    },
                    vLineWidth: function (i, node) {
                        return 0.5;
                    }
                }
            } : null,

            isVisible(customizationFields, 'body_totals_table_total_due') ? getTotalDueBox(total, customizationFields) : null,
        ].filter(node => !! node)
    }

    const getFilterLines = (date, payload, customizationFields) => {
        return [
            { key: 'body_top_left_date_range', text: `Date: ${date.startDate} - ${date.endDate}` },
            { key: 'body_top_left_business_unit', text: `${payload.businessUnit ? 'Business Unit: ' + payload.businessUnit : ''}` },
            { key: 'body_top_left_customer', text: `${payload.customerName ? 'Customer: ' + payload.customerName : ''}` },
            { key: 'body_top_left_client', text: payload.clientName ? { text: 'Client: ' + payload.clientName, bold: true, fontSize: resolveFontSize(customizationFields, defaultFontSize) + 1 } : '' },
            { key: 'body_top_left_bill_type', text: `${payload.billType ? 'Bill Type: ' + payload.billType : ''}` },
            { key: 'body_top_left_order_type', text: `${payload.orderType ? 'Order Type: ' + payload.orderType : ''}` },
            { key: 'body_top_left_payment_status', text: `${payload.paymentStatus ? 'Payment Status: ' + payload.paymentStatus : ''}` },
        ].filter(line => isVisible(customizationFields, line.key)).map(line => ({ text: line.text }))
    }

    const getTableColumns = (customizationFields, moneyReceipt) => {
        return [
            { key: 'body_table_invoice_no', header: 'INVOICE NO', cell: child => child.bill_number },
            { key: 'body_table_invoice_date', header: 'INVOICE DATE', cell: child => child.date },
            { key: 'body_table_client_name', header: 'PATIENT NAME', cell: child => child.contact_name },
            { key: 'body_table_client_id', header: 'PATIENT ID', cell: child => child.contact_profile_id },
            { key: 'body_table_item_details', header: 'ITEM DETAILS', width: '*', cell: child => child.item_details, when: rows => hasItemDetails(rows) },
            { key: 'body_table_total_amount', header: 'TOTAL AMOUNT', isAmount: true, cell: child => commaFormat(getTotalAmount(child)), total: total => total.total_amount_with_points },
            { key: 'body_table_discount_amount', header: 'DISCOUNT AMOUNT', isAmount: true, cell: child => commaFormat(child.discount), total: total => total.total_discount_amount },
            { key: 'body_table_net_amount', header: 'NET AMOUNT', isAmount: true, cell: child => commaFormat(getTotalAmount(child) - child.discount), total: total => commaFormat(getNetAmount(total)) },
            { key: 'body_table_adjusted_amount', header: 'ADJUSTED AMOUNT', isAmount: true, cell: child => commaFormat(getAdjustedAmount(child)), total: (total, rows) => getTotalAdjustedAmount(rows) },
            { key: 'body_table_paid_amount', header: 'PAID AMOUNT', isAmount: true, cell: child => commaFormat(child.paid_amount), total: total => total.total_paid_amount },
            { key: 'body_table_due_amount', header: 'DUE AMOUNT', isAmount: true, cell: child => commaFormat(getDue(child)), total: total => total.total_due_amount },
        ].filter(column => isVisible(customizationFields, column.key) && (! column.when || column.when(moneyReceipt)))
    }

    const hasItemDetails = (rows) => rows.some(row => row.item_details !== undefined);

    const toCell = (column, text) => column.isAmount ? { text: text, alignment: 'right' } : { text: text };

    const getTableBody = (columns, moneyReceipt, total, customizationFields) => {
        const header = columns.map(column => toCell(column, column.header));
        const rows = moneyReceipt.map(child => columns.map(column => toCell(column, column.cell(child))));
        if (! isVisible(customizationFields, 'body_totals_table_total_row')) return [header, ...rows];
        if (! columns.some(column => column.isAmount)) return [header, ...rows];
        return [header, ...rows, getTotalRow(columns, total, moneyReceipt)];
    }

    const getTotalRow = (columns, total, moneyReceipt) => {
        const labelSpan = columns.findIndex(column => column.isAmount);
        const totalCells = columns.slice(labelSpan).map(column => ({ text: column.total ? column.total(total, moneyReceipt) : '', alignment: 'right', bold: true }));
        if (labelSpan === 0) return totalCells;
        const placeholders = Array.from({ length: labelSpan - 1 }, () => ({}));
        return [{ text: `Total`, colSpan: labelSpan, alignment: 'right', bold: true }, ...placeholders, ...totalCells];
    }

    const getTotalDueBox = (total, customizationFields) => ({
        margin: [0, 10, 0, 0],
        columns: [
            { width: '*', text: '' },
            {
                width: 'auto',
                table: {
                    body: [[{ text: `Total Due Amount: ${total.total_due_amount}`, bold: true, fontSize: resolveFontSize(customizationFields, defaultFontSize) + 3, margin: [8, 4, 8, 4] }]]
                },
                layout: { hLineWidth: () => 0.5, vLineWidth: () => 0.5 }
            }
        ]
    })

    const getHeader = (company, customizationFields, pageMargins) => {
        const coverPhoto = getActiveSetting(customizationFields, 'extra_settings_cover_photo_upload');

        if (coverPhoto && coverPhoto.value64) {
            return {
                columns: [
                    {
                        image: coverPhoto.value64,
                        width: pageWidth,
                        height: pageMargins[1] || defaultPageMargins[1],
                        alignment: 'center'
                    }
                ]
            }
        }

        return {
            margin: [ 30, 25, 30, 10 ],
            columns: [
                isVisible(customizationFields, 'heading_left_logo') ? {
                    alignment: 'left',
                    image: company.logo64,
                    maxHeight: 60
                } : null,

                {
                    alignment: 'right',
                    stack: getCompanyLines(company, customizationFields)
                }
            ].filter(column => !! column)
        }
    }

    const getCompanyLines = (company, customizationFields) => {
        return [
            { key: 'heading_right_company_name', text: company.name },
            { key: 'heading_right_company_address', text: 'Address: ' + company.address },
            { key: 'heading_right_company_phone', text: 'Phone: ' + company.phone },
            { key: 'heading_right_company_email', text: 'E-mail: ' + company.email },
        ].filter(line => isVisible(customizationFields, line.key)).map(line => line.text)
    }

    const getFooterNote = (customizationFields) => {
        const setting = getActiveSetting(customizationFields, 'extra_settings_footer_note');
        if (! setting) return 'N.B: This is a system generated documents and requires no manual signature.';
        return setting.value || 'N.B: This is a system generated documents and requires no manual signature.';
    }

    const getFooter = (userName, customizationFields, pageMargins) => {
        const footerPhoto = getActiveSetting(customizationFields, 'extra_settings_footer_photo_upload');

        if (footerPhoto && footerPhoto.value64) {
            return {
                columns: [
                    {
                        image: footerPhoto.value64,
                        width: pageWidth,
                        height: pageMargins[3] || defaultPageMargins[3],
                        alignment: 'center'
                    }
                ]
            }
        }

        return {
            margin: [ 25, 0 ],
            columns: [
                isVisible(customizationFields, 'footer_note') ? {
                    columns: [
                        {
                            width: '90%',
                            alignment: 'left',
                            text: getFooterNote(customizationFields),
                            margin: [5, 53, 0, 0],
                            fontSize: 8
                        }
                    ]
                } : null,

                isVisible(customizationFields, 'footer_printed_by') ? {
                    width: '20%',
                    alignment: 'right',
                    fontSize: 8,
                    margin: [0, 33, 0, 0],
                    stack: [
                        {
                            text: 'Printed by',
                            bold: true
                        },
                        {
                            text: userName
                        },
                        {
                            text: formatDate(new Date())
                        }
                    ]
                } : null
            ].filter(column => !! column)
        }
    }

    const getDue = (item) => {
        return parseFloat(item.total_amount) -
                parseFloat(item.paid_amount) -
                parseFloat(item.adjusted_amount);
    }

    const getTotalAmount = (item) => {
        const pointsRedeemed = item.points_redeemed ? parseFloat(item.points_redeemed) : 0
        return parseFloat(item.total_amount) + pointsRedeemed
    }

    const getAdjustedAmount = (item) => {
        const pointsRedeemed = item.points_redeemed ? parseFloat(item.points_redeemed) : 0
        return parseFloat(item.adjusted_amount) - parseFloat(item.discount) + pointsRedeemed
    }

    const getTotalAdjustedAmount = (items) => {
        return commaFormat(items.reduce((sum, item) => sum + getAdjustedAmount(item), 0))
    }

    const getNetAmount = (total) => {
        const amount = parseFloat(total.total_amount_with_points.replace(/,/g, ''));
        const discount = parseFloat(total.total_discount_amount.replace(/,/g, ''));
        return amount - discount;
    }

    return {
        exportToPDF
    }
}

export default pdfPrinter;
